import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components/macro'
import { useNavigate, useLocation } from 'react-router-dom'
import { LeftNavIconButton } from './LeftNavIconButton'
import { useLeftNavStore } from './left-nav-store'
import { useAuth } from 'context/authentication/useAuth'
import { featureFlagService } from 'utils/featureFlagService'
import { Z_INDEX } from 'styles/z-index'
import type { NavItem, NavSection, NavIconType } from './types'

const NavWrapper = styled.nav<{ $isCollapsed: boolean }>`
  display: ${({ $isCollapsed }) => ($isCollapsed ? 'none' : 'flex')};
  flex-direction: column;
  width: var(--left-nav-width);
  min-width: var(--left-nav-width);
  height: 100%;
  background: var(--nav-bg-primary);
  padding: 0 16px 24px;
  z-index: ${Z_INDEX.LEFT_NAV};
`

const NavContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  justify-content: space-between;
  min-height: 0;
`

const TopSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const BottomSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const Divider = styled.div`
  width: 44px;
  height: 1px;
  background: var(--nav-text-primary);
  opacity: 0.5;
  margin: 8px 0;
`

// Define navigation items with their configurations
const NAV_ITEMS: NavItem[] = [
  {
    id: 'targets',
    label: 'Targets',
    icon: 'target',
    path: '/opportunities',
    hasSlideOut: true,
  },
  {
    id: 'campaigns',
    label: 'Campaigns',
    icon: 'megaphone',
    path: '/campaigns',
    hasSlideOut: false,
  },
  {
    id: 'samples',
    label: 'Samples',
    icon: 'hand-coins',
    path: '/samples',
    hasSlideOut: false,
  },
  {
    id: 'rebates',
    label: 'Rebates',
    icon: 'seal-percent',
    path: '/rebates',
    hasSlideOut: false,
  },
  {
    id: 'deals',
    label: 'Deals',
    icon: 'handshake',
    path: '/deals',
    hasSlideOut: false,
  },
  {
    id: 'performance',
    label: 'Performance',
    icon: 'chart-line-up',
    path: '/dashboard',
    hasSlideOut: false,
  },
  {
    id: 'workspace',
    label: 'Workspace',
    icon: 'workspace',
    path: '/documents',
    hasSlideOut: true,
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: 'gear',
    path: '/settings',
    hasSlideOut: true,
  },
]

export const LeftNavBar: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { logout } = useAuth()
  const featureFlags = featureFlagService()

  const {
    isCollapsed,
    setActiveSection,
    isSlideOutOpen,
    openSlideOut,
    closeSlideOut,
    lastVisited,
  } = useLeftNavStore()

  // Filter nav items based on feature flags
  const visibleNavItems = useMemo(() => {
    return NAV_ITEMS.filter((item) => {
      if (item.id === 'samples' && !featureFlags.enableSamples) return false
      if (item.id === 'campaigns' && !featureFlags.enableCampaigns) return false
      return true
    })
  }, [featureFlags])

  // Determine active section from current path
  const currentActiveSection = useMemo((): NavSection | null => {
    const path = location.pathname

    // Workspace section includes documents, contacts, and activity
    if (
      path.startsWith('/documents') ||
      path.startsWith('/contacts') ||
      path.startsWith('/activity')
    ) {
      return 'workspace'
    }

    // Check each nav item to see if current path matches
    for (const item of NAV_ITEMS) {
      if (path.startsWith(item.path)) {
        return item.id
      }
    }

    return null
  }, [location.pathname])

  const handleNavClick = useCallback(
    (item: NavItem) => {
      const isCurrentlyActive = currentActiveSection === item.id

      if (item.hasSlideOut) {
        // For sections with slide-out panels
        if (isCurrentlyActive && isSlideOutOpen) {
          // Close slide-out if clicking the already active section with panel open
          closeSlideOut()
        } else {
          // Open slide-out for this section
          openSlideOut(item.id)
        }
      } else {
        // For sections without slide-out panels, navigate directly
        setActiveSection(item.id)
        closeSlideOut()
        navigate(lastVisited[item.id] || item.path)
      }
    },
    [
      currentActiveSection,
      isSlideOutOpen,
      setActiveSection,
      openSlideOut,
      closeSlideOut,
      navigate,
      lastVisited,
    ]
  )

  const handleHelpClick = useCallback(() => {
    window.open('https://help.firstbite.io/', '_blank')
  }, [])

  const handleLogoutClick = useCallback(() => {
    logout()
  }, [logout])

  return (
    <NavWrapper
      $isCollapsed={isCollapsed}
      aria-label="Main navigation"
      data-left-nav
    >
      <NavContent>
        <TopSection>
          {visibleNavItems.map((item) => (
            <LeftNavIconButton
              key={item.id}
              icon={item.icon as NavIconType}
              label={item.label}
              isSelected={currentActiveSection === item.id}
              onClick={() => handleNavClick(item)}
            />
          ))}
        </TopSection>

        <BottomSection>
          <LeftNavIconButton
            icon="question"
            label="Help Center"
            onClick={handleHelpClick}
          />
          <Divider />
          <LeftNavIconButton
            icon="sign-out"
            label="Log Out"
            onClick={handleLogoutClick}
          />
        </BottomSection>
      </NavContent>
    </NavWrapper>
  )
}
